import { z } from "zod";
// 🔴 MỘT định nghĩa trạng thái link, không hai. Enum `active|expired|revoked` đã tồn tại ở file
// admin (viết trước, US-AE13.F2-0045); import lại thay vì chép ra một bản thứ hai — hai enum cùng
// nghĩa là hai chỗ có thể trôi khỏi nhau, và trôi ở đây nghĩa là UI nói link "còn sống" trong khi
// server coi là đã thu hồi. File contracts là các sibling phẳng, import này không phá tầng nào.
import {
  transparencyLinkStatusSchema,
  type TransparencyLinkStatus,
} from "./admin-transparency-quiet.js";

/* ===========================================================================
 * US-E10.F3-0063 Transparency Link (@UC0024 manage-transparency-link /
 * @UC0025 configure-transparency-scope) — Phase 9 (@gate rbac,pii).
 *
 * S01 CRUD (OWNER-only): POST /v1/groups/:id/transparency-links (tạo link +
 *   token plaintext 1 lần), DELETE /:lid (revoke → token invalid ngay).
 * S02 public view: GET /v1/t/:token (redaction fail-closed theo scope, 404 đồng
 *   nhất chống enumeration, CSP frame-ancestors 'none', rate-limit).
 *
 * Scope matrix (PO chốt §6 — 3 keys, PII LUÔN ẩn):
 *  - show_balance       → số dư quỹ (STRING minor unit, B1 no-float)
 *  - show_history       → danh sách giao dịch gần (KHÔNG kèm tên/PII người)
 *  - show_member_status → tên member + trạng thái đóng/chưa (KHÔNG phone/email)
 * phone/email/địa chỉ KHÔNG có scope key nào bật ⇒ KHÔNG BAO GIỜ trả (fail-closed).
 * scope phải có ≥1 key true, else 400 (fail-closed — không tạo link "rỗng").
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Ba key scope hợp lệ (allowlist đóng). Không key nào bật phone/email/địa chỉ. */
export const TRANSPARENCY_SCOPE_KEYS = [
  "show_balance",
  "show_history",
  "show_member_status",
] as const;

export type TransparencyScopeKey = (typeof TRANSPARENCY_SCOPE_KEYS)[number];

/**
 * Scope object. `.strict()` → key lạ (vd show_phone, show_email) → 400. Mọi key optional
 * boolean; refine (ở body schema) ép ≥1 key true để chống link rỗng vô nghĩa.
 */
export const transparencyScopeSchema = z
  .object({
    show_balance: z.boolean().optional(),
    show_history: z.boolean().optional(),
    show_member_status: z.boolean().optional(),
  })
  .strict();

export type TransparencyScope = z.infer<typeof transparencyScopeSchema>;

/** ≥1 key được bật (true). Fail-closed: scope rỗng / tất cả false → 400. */
export function hasAnyScopeEnabled(scope: TransparencyScope): boolean {
  return TRANSPARENCY_SCOPE_KEYS.some((k) => scope[k] === true);
}

/**
 * Body tạo link. `.strict()` chặn field lạ ở cấp ngoài; scope `.strict()` chặn key lạ.
 * refine: scope phải có ≥1 key true (fail-closed → 400 nếu rỗng / toàn false).
 */
export const createTransparencyLinkBodySchema = z
  .object({
    scope: transparencyScopeSchema,
  })
  .strict()
  .refine((b) => hasAnyScopeEnabled(b.scope), {
    message: "scope phải bật ít nhất 1 key",
    path: ["scope"],
  });

export type CreateTransparencyLinkBody = z.infer<
  typeof createTransparencyLinkBodySchema
>;

/**
 * Response tạo link. `token` = plaintext CHỈ trả 1 lần (client render link/QR). Server
 * KHÔNG lưu plaintext (chỉ tokenHash). `scope` echo lại các key đã lưu.
 */
export const createTransparencyLinkResponseSchema = z
  .object({
    id: z.string().uuid(),
    /** Token plaintext — chỉ xuất hiện 1 lần trong response này, không lưu DB. */
    token: z.string().min(1),
    scope: transparencyScopeSchema,
    createdAt: z.string().datetime(),
  })
  .strict();

export type CreateTransparencyLinkResponse = z.infer<
  typeof createTransparencyLinkResponseSchema
>;

/* ===========================================================================
 * US-E24.F10-0359-S02 (@UC9599) — GET /v1/groups/:id/transparency-links.
 *
 * VÌ SAO CÓ: trước task này, `lid` chỉ tồn tại trong response POST tạo. Đóng màn là mất `lid`, mà
 * `lid` là tham số DUY NHẤT của `DELETE .../transparency-links/:lid` ⇒ **mất luôn quyền thu hồi**
 * một URL công khai không cần đăng nhập đang phơi tình hình tài chính của nhóm.
 *
 * 🔴 BẤT BIẾN SỐ MỘT: KHÔNG `token`, KHÔNG `tokenHash`.
 *  - `token` thô không tồn tại trong DB (chỉ hash) — không có gì để trả kể cả muốn.
 *  - `tokenHash` CÓ trong DB và đây chính là cái bẫy: nó *trông như* metadata vô hại. Không phải —
 *    `getPublicView` tra THẲNG bằng `tokenHash`. Rò hash là rò đường vào, và làm hỏng 404-đồng-nhất
 *    chống token-oracle. `.strict()` dưới đây là lưới TẠI BIÊN KIỂU cho đúng chuyện đó.
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const TRANSPARENCY_LINKS_MAX_LIMIT = 50;
export const TRANSPARENCY_LINKS_MAX_CURSOR_LENGTH = 512;

/**
 * Trạng thái dẫn xuất, TÍNH TẠI THỜI ĐIỂM ĐỌC từ (revokedAt, expiresAt) — để UI khỏi tự suy từ hai
 * timestamp rồi suy sai. Thứ tự ưu tiên `revoked > expired > active`: thu hồi là hành động CỦA NGƯỜI
 * và là khoá cứng, hết hạn chỉ là thời gian trôi.
 *
 * ⚠️ `admin-transparency.service.ts` có một `deriveStatus` private cùng ngữ nghĩa (lane admin, viết
 * trước). Task này KHÔNG được đụng lane admin (index mục 2) nên chưa gộp được; hàm thuần dưới đây là
 * nơi các surface MỚI phải dùng, và là chỗ để gộp bản admin vào khi có task chạm lane đó.
 */
export function deriveTransparencyLinkStatus(
  row: { readonly revokedAt: Date | null; readonly expiresAt: Date | null },
  now: Date,
): TransparencyLinkStatus {
  if (row.revokedAt !== null) return "revoked";
  if (row.expiresAt !== null && row.expiresAt.getTime() < now.getTime()) return "expired";
  return "active";
}

/**
 * Lọc cột `scope Json` về ĐÚNG allowlist 3 khoá boolean (fail-closed).
 *
 * 🔴 `.strict()` KHÔNG bảo vệ được nội dung một cột `Json` — hàng cũ/xấu có thể mang `show_phone`,
 * và dump thô là rò thẳng ra response. Hàm thuần, KHÔNG BAO GIỜ ném: một hàng scope hỏng không được
 * phép làm sập cả trang danh sách (mà danh sách này chính là đường thu hồi link đang rò).
 */
export function pickTransparencyScope(raw: unknown): TransparencyScope {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const obj = raw as Record<string, unknown>;
  const out: Record<string, boolean> = {};
  for (const key of TRANSPARENCY_SCOPE_KEYS) {
    if (typeof obj[key] === "boolean") out[key] = obj[key] as boolean;
  }
  return out as TransparencyScope;
}

/**
 * 1 dòng link trong danh sách — METADATA THUẦN. `lid` đặt tên trùng param của route DELETE
 * (`.../transparency-links/:lid`) để không ai phải đoán field nào ghép vào URL nào.
 * KHÔNG `lastAccessedAt`: cột đó KHÔNG TỒN TẠI trong schema (model chỉ 8 cột) — thêm nghĩa là
 * migration + 1 UPDATE mỗi lượt xem trên endpoint CÔNG KHAI không auth (mở surface DoS).
 */
export const transparencyLinkListItemSchema = z
  .object({
    lid: z.string().uuid(),
    scope: transparencyScopeSchema,
    status: transparencyLinkStatusSchema,
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
    createdBy: z.string().uuid().nullable(),
  })
  .strict();

export type TransparencyLinkListItem = z.infer<typeof transparencyLinkListItemSchema>;

/** Envelope cursor chuẩn repo. Nhóm chưa từng tạo link ⇒ `data: []` (KHÔNG 404). */
export const transparencyLinksListResponseSchema = z
  .object({
    data: z.array(transparencyLinkListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type TransparencyLinksListResponse = z.infer<
  typeof transparencyLinksListResponseSchema
>;

/** Query phân trang. Cursor rác ⇒ 400 ở service (không để rơi vào Prisma thành P2023/500). */
export const transparencyLinksListQuerySchema = z.object({
  cursor: z.string().max(TRANSPARENCY_LINKS_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(TRANSPARENCY_LINKS_MAX_LIMIT).optional(),
});

export type TransparencyLinksListQuery = z.infer<
  typeof transparencyLinksListQuerySchema
>;

/* --- S02 public view ----------------------------------------------------- */

/** Message ĐỒNG NHẤT cho not-found / revoked / expired (chống enumeration). */
export const TRANSPARENCY_LINK_GONE_MESSAGE = "Link không còn hiệu lực";

/**
 * 1 giao dịch trong public history — KHÔNG kèm tên/PII người (chỉ loại + số + thời điểm).
 * Review M1: KHÔNG expose `memo` (free-text người dùng nhập → có thể chứa tên/PII/số ĐT) trên endpoint
 * PUBLIC unauth. Story "show_history → KHÔNG PII người" ⇒ fail-closed: bỏ memo khỏi public shape.
 */
export const publicTransactionSchema = z
  .object({
    id: z.string().uuid(),
    txType: z.string(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    createdAt: z.string().datetime(),
  })
  .strict();

export type PublicTransaction = z.infer<typeof publicTransactionSchema>;

/** 1 member trong public member-status — CHỈ tên + đã đóng chưa; KHÔNG phone/email. */
export const publicMemberStatusSchema = z
  .object({
    name: z.string(),
    hasPaid: z.boolean(),
  })
  .strict();

export type PublicMemberStatus = z.infer<typeof publicMemberStatusSchema>;

/**
 * Response public view — mọi field theo scope OPTIONAL (OMIT khi scope không bật, fail-closed).
 * KHÔNG bao giờ có phone/email/địa chỉ. `.strict()` bảo đảm không rò field ngoài allowlist.
 */
export const transparencyPublicViewResponseSchema = z
  .object({
    groupName: z.string(),
    currency: z.string().length(3),
    /** show_balance → số dư CASH (STRING minor unit). OMIT khi không bật. */
    balance: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    /** show_history → giao dịch gần (KHÔNG tên người). OMIT khi không bật. */
    history: z.array(publicTransactionSchema).optional(),
    /** show_member_status → tên + trạng thái đóng (KHÔNG phone/email). OMIT khi không bật. */
    memberStatus: z.array(publicMemberStatusSchema).optional(),
  })
  .strict();

export type TransparencyPublicViewResponse = z.infer<
  typeof transparencyPublicViewResponseSchema
>;

/** Dữ liệu quỹ đọc từ DB TRƯỚC redaction (đầy đủ; redaction là allowlist pure trên object này). */
export interface TransparencyPublicViewData {
  readonly groupName: string;
  readonly currency: string;
  readonly balance: string;
  readonly history: readonly PublicTransaction[];
  readonly memberStatus: readonly PublicMemberStatus[];
}

/**
 * Redaction PURE (allowlist fail-closed): chỉ field mà scope chủ động bật (true) mới vào
 * response. groupName + currency luôn hiển thị (metadata không nhạy cảm). Không key nào lộ
 * phone/email/địa chỉ. Deterministic → dễ unit test mọi nhánh.
 */
export function redactPublicView(
  scope: TransparencyScope,
  data: TransparencyPublicViewData,
): TransparencyPublicViewResponse {
  const out: TransparencyPublicViewResponse = {
    groupName: data.groupName,
    currency: data.currency,
  };
  if (scope.show_balance === true) out.balance = data.balance;
  if (scope.show_history === true) out.history = [...data.history];
  if (scope.show_member_status === true) {
    out.memberStatus = data.memberStatus.map((m) => ({ ...m }));
  }
  return out;
}
